import { formatNumber } from './commonFunction';

// Cohesive categorical palette for entitlement usage charts. Leads with the aqfer
// brand blue and moves through teal/green/amber so stacked series stay distinguishable.
export const CHART_PALETTE = [
    '#2C7BE5', // brand blue
    '#00B8A9', // teal
    '#39AFD1', // sky
    '#00D97E', // green
    '#F6C343', // amber
    '#6E84A3', // slate
    '#A66DD4', // purple
    '#FD7E14', // orange
];

// Neutral slate tones so axes and gridlines stay legible in both light and dark themes.
const AXIS_COLOR = '#95AAC9';
const GRID_COLOR = 'rgba(128, 141, 161, 0.18)';
// Neutral slate for the entitlement threshold line — a calm reference marker rather
// than an alarm (over-limit is signalled in red on the summary bar instead).
const LIMIT_COLOR = '#647287';
const TOTAL_COLOR = '#12263F';

export function colorFor(index) {
    return CHART_PALETTE[index % CHART_PALETTE.length];
}

// Common styling applied to every dataset (rounded bars, consistent color).
export function styleDataset(dataset, index) {
    const color = colorFor(index);

    // Drop zero/negative points so we never draw phantom segments (and so the historical
    // chart discards its mislabelled zero placeholders). We deliberately do NOT set a
    // minBarLength: forcing a per-segment minimum height makes a stack of many small
    // sub-tenant segments render taller than its true total — enough to appear to cross
    // the entitlement line when it hasn't. Exact per-segment values live in the table.
    const yKey = dataset.parsing && dataset.parsing.yAxisKey;
    const data = Array.isArray(dataset.data) && yKey
        ? dataset.data.filter(point => point && Number(point[yKey]) > 0)
        : dataset.data;

    return {
        ...dataset,
        data,
        backgroundColor: color,
        hoverBackgroundColor: color,
        borderColor: color,
        borderWidth: 0,
        borderRadius: 4,
        borderSkipped: false,
        maxBarThickness: 96,
        categoryPercentage: 0.6,
        barPercentage: 0.9,
    };
}

// Shared x/y scale config for the stacked usage bar charts.
export function barScales() {
    return {
        x: {
            stacked: true,
            grid: { display: false },
            border: { display: false },
            ticks: { color: AXIS_COLOR, font: { size: 11 } },
        },
        y: {
            stacked: true,
            beginAtZero: true,
            grid: { color: GRID_COLOR },
            border: { display: false },
            ticks: {
                color: AXIS_COLOR,
                font: { size: 11 },
                callback: (value) => formatNumber(value),
            },
        },
    };
}

// Dark, rounded tooltip that matches the rest of the portal.
export function tooltipStyle(callbacks = {}) {
    return {
        backgroundColor: 'rgba(18, 38, 63, 0.92)',
        padding: 10,
        cornerRadius: 6,
        titleColor: '#ffffff',
        bodyColor: '#e1e6ec',
        usePointStyle: true,
        callbacks,
    };
}

// Draws a dashed entitlement-limit line with a small right-aligned label, on top of
// the bars. Reads the threshold from options.plugins.limitLine.limit.
export const limitLinePlugin = {
    id: 'limitLine',
    afterDatasetsDraw(chart, args, options) {
        const limit = options.limit;
        if (limit == null || !isFinite(limit)) return;

        const { ctx, chartArea, scales } = chart;
        const y = scales.y.getPixelForValue(limit);
        // Skip when the limit falls outside the visible range.
        if (y < chartArea.top || y > chartArea.bottom) return;

        ctx.save();

        // Dashed line
        ctx.beginPath();
        ctx.moveTo(chartArea.left, y);
        ctx.lineTo(chartArea.right, y);
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = LIMIT_COLOR;
        ctx.setLineDash([4, 4]);
        ctx.stroke();

        // Pill label
        const label = `${options.label || 'Current entitlement'} ${formatNumber(limit)}`;
        ctx.setLineDash([]);
        ctx.font = '600 11px sans-serif';
        const padX = 6;
        const boxH = 18;
        const boxW = ctx.measureText(label).width + padX * 2;
        const boxX = chartArea.right - boxW;
        const boxY = Math.max(chartArea.top, y - boxH - 2);
        const r = 4;

        ctx.beginPath();
        ctx.moveTo(boxX + r, boxY);
        ctx.arcTo(boxX + boxW, boxY, boxX + boxW, boxY + boxH, r);
        ctx.arcTo(boxX + boxW, boxY + boxH, boxX, boxY + boxH, r);
        ctx.arcTo(boxX, boxY + boxH, boxX, boxY, r);
        ctx.arcTo(boxX, boxY, boxX + boxW, boxY, r);
        ctx.closePath();
        ctx.fillStyle = LIMIT_COLOR;
        ctx.fill();

        ctx.fillStyle = '#ffffff';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, boxX + padX, boxY + boxH / 2 + 0.5);

        ctx.restore();
    },
};

// Draws a "full count" cap line across the top of each stacked bar with the summed
// value beside it — so the total consumption of everything in the stack is visible in
// the chart (per sub-entitlement in breakdown mode). Totals come from the caller as
// options.plugins.stackTotal.totals: [{ x: <category index>, value: <stack total> }].
//
// The cap is drawn at the RENDERED top of the stack (not the value's axis pixel) so it
// always sits exactly on the bar: with minBarLength lifting sub-pixel segments, a stack
// renders slightly taller than its true sum, and a line at the true value would float
// below the bar top. The label still shows the true summed value.
export const stackTotalPlugin = {
    id: 'stackTotal',
    afterDatasetsDraw(chart, args, options) {
        const totals = options && options.totals;
        if (!Array.isArray(totals) || !totals.length) return;

        const { ctx, chartArea } = chart;

        // Group rendered bar geometry by x-centre. Data may be filtered/sparse, so we
        // read actual element positions rather than trusting index alignment.
        const byX = new Map();
        for (let di = 0; di < chart.data.datasets.length; di++) {
            const meta = chart.getDatasetMeta(di);
            if (!meta || meta.hidden || !meta.data) continue;
            for (const el of meta.data) {
                if (!el || typeof el.x !== 'number' || typeof el.y !== 'number') continue;
                const kx = Math.round(el.x);
                const cur = byX.get(kx) || { x: el.x, topY: Infinity, width: 0 };
                if (el.y < cur.topY) cur.topY = el.y;
                if (el.width) cur.width = Math.max(cur.width, el.width);
                byX.set(kx, cur);
            }
        }
        if (!byX.size) return;

        // Rendered stacks left-to-right, paired positionally with the non-zero totals in
        // category order. This is robust to filtered/sparse data (a zero category renders
        // no bar and also has a zero total, so both drop out and the order stays aligned).
        const stacks = Array.from(byX.values()).sort((a, b) => a.x - b.x);
        const values = totals.filter(t => t && isFinite(t.value) && t.value > 0);

        ctx.save();
        const pairs = Math.min(stacks.length, values.length);
        for (let i = 0; i < pairs; i++) {
            const stack = stacks[i];
            const value = values[i].value;
            const topY = Math.max(stack.topY, chartArea.top);
            const half = (stack.width || 40) / 2 + 5; // extend a touch beyond the bar

            ctx.beginPath();
            ctx.moveTo(stack.x - half, topY);
            ctx.lineTo(stack.x + half, topY);
            ctx.lineWidth = 2;
            ctx.strokeStyle = TOTAL_COLOR;
            ctx.stroke();

            ctx.font = '600 11px sans-serif';
            ctx.fillStyle = TOTAL_COLOR;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            ctx.fillText(formatNumber(value), stack.x, topY - 4);
        }
        ctx.restore();
        ctx.textAlign = 'start';
    },
};
